import { useCallback, useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { ImageUploadField } from '../components/ImageUploadField';
import { bannerService } from '../services/dashboardService';
import { fieldClass, labelClass } from '../utils/form';

type BannerPlacement = 'homepage_hero' | 'homepage_promo';
type BannerTheme = 'teal' | 'amber';

type BannerRow = {
  id: string;
  title: string;
  description: string;
  badge: string;
  image: string;
  targetUrl: string;
  ctaText: string;
  placement: BannerPlacement;
  theme: BannerTheme;
  order: number;
  isActive: boolean;
  createdAt?: string;
};

type BannerFormState = {
  title: string;
  description: string;
  badge: string;
  image: string;
  targetUrl: string;
  ctaText: string;
  placement: BannerPlacement;
  theme: BannerTheme;
  order: number;
  isActive: boolean;
};

const emptyForm = (): BannerFormState => ({
  title: '',
  description: '',
  badge: '',
  image: '',
  targetUrl: '/products',
  ctaText: 'مشاهده',
  placement: 'homepage_hero',
  theme: 'teal',
  order: 0,
  isActive: true,
});

const placementLabels: Record<BannerPlacement, string> = {
  homepage_hero: 'هیرو صفحه اصلی',
  homepage_promo: 'پرومو صفحه اصلی',
};

function num(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function mapBanner(raw: Record<string, unknown>): BannerRow {
  const placementRaw = String(raw.placement ?? 'homepage_hero');
  const placement: BannerPlacement =
    placementRaw === 'homepage_promo' ? 'homepage_promo' : 'homepage_hero';
  const themeRaw = String(raw.theme ?? 'teal');
  const theme: BannerTheme = themeRaw === 'amber' ? 'amber' : 'teal';

  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    description: String(raw.description ?? raw.subtitle ?? ''),
    badge: String(raw.badge ?? ''),
    image: String(raw.image ?? ''),
    targetUrl: String(raw.targetUrl ?? raw.ctaLink ?? ''),
    ctaText: String(raw.ctaText ?? 'مشاهده'),
    placement,
    theme,
    order: num(raw.order),
    isActive: Boolean(raw.isActive),
    createdAt: raw.createdAt ? String(raw.createdAt) : undefined,
  };
}

function formFromRow(row: BannerRow): BannerFormState {
  return {
    title: row.title,
    description: row.description,
    badge: row.badge,
    image: row.image,
    targetUrl: row.targetUrl,
    ctaText: row.ctaText,
    placement: row.placement,
    theme: row.theme,
    order: row.order,
    isActive: row.isActive,
  };
}

function payloadFromForm(form: BannerFormState): Record<string, unknown> {
  return {
    title: form.title.trim(),
    description: form.description.trim(),
    subtitle: form.description.trim(),
    badge: form.badge.trim(),
    image: form.image.trim(),
    targetUrl: form.targetUrl.trim() || '/products',
    ctaLink: form.targetUrl.trim() || '/products',
    ctaText: form.ctaText.trim() || 'مشاهده',
    placement: form.placement,
    theme: form.theme,
    order: num(form.order),
    isActive: form.isActive,
  };
}

export function BannersPage() {
  const [banners, setBanners] = useState<BannerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editing, setEditing] = useState<BannerRow | null>(null);
  const [viewing, setViewing] = useState<BannerRow | null>(null);
  const [form, setForm] = useState<BannerFormState>(emptyForm);

  const loadBanners = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bannerService.list();
      setBanners((data as unknown as Record<string, unknown>[]).map(mapBanner));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت بنرها');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBanners();
  }, [loadBanners]);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (row: BannerRow) => {
    setEditing(row);
    setForm(formFromRow(row));
    setFormOpen(true);
  };

  const openView = (row: BannerRow) => {
    setViewing(row);
    setViewOpen(true);
  };

  const closeView = () => {
    setViewOpen(false);
    setViewing(null);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('عنوان بنر الزامی است');
      return;
    }
    if (!form.image.trim()) {
      toast.error('تصویر بنر الزامی است');
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      if (editing) {
        await bannerService.update(editing.id, payload);
        toast.success('بنر به‌روزرسانی شد');
      } else {
        await bannerService.create(payload);
        toast.success('بنر ایجاد شد');
      }
      closeForm();
      await loadBanners();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره بنر ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: BannerRow) => {
    if (!window.confirm(`بنر «${row.title}» حذف شود؟`)) return;
    try {
      await bannerService.remove(row.id);
      toast.success('بنر حذف شد');
      await loadBanners();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف ناموفق بود');
    }
  };

  const setField = <K extends keyof BannerFormState>(key: K, value: BannerFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">بنرها</h1>
          <p className="text-slate-600 mt-1">هیرو و پروموهای صفحه اصلی فروشگاه</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="w-full sm:w-auto flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          <Plus className="h-4 w-4" />
          افزودن بنر
        </button>
      </div>

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-500">
          در حال بارگذاری...
        </div>
      ) : banners.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          هنوز بنری ثبت نشده. اولین بنر هیرو را اضافه کنید تا در صفحه اصلی دیده شود.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {banners.map((banner) => (
            <div
              key={banner.id}
              className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
            >
              {banner.image ? (
                <img src={banner.image} alt="" className="h-40 w-full object-cover" />
              ) : (
                <div className="flex h-40 items-center justify-center bg-slate-100 text-slate-400 text-sm">
                  بدون تصویر
                </div>
              )}
              <div className="space-y-3 p-4">
                <div>
                  <h3 className="font-semibold text-slate-900">{banner.title}</h3>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{banner.description || '—'}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                    {placementLabels[banner.placement]}
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-1 font-medium ${
                      banner.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {banner.isActive ? 'فعال' : 'غیرفعال'}
                  </span>
                  <span className="text-slate-500">ترتیب: {banner.order}</span>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => openView(banner)}
                    className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
                    title="مشاهده"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(banner)}
                    className="rounded-lg border border-slate-200 p-2 text-blue-600 hover:bg-blue-50"
                    title="ویرایش"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(banner)}
                    className="rounded-lg border border-slate-200 p-2 text-red-600 hover:bg-red-50"
                    title="حذف"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? 'ویرایش بنر' : 'افزودن بنر'}
        size="lg"
        footer={
          <>
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
          </>
        }
      >
        <div className="space-y-4" dir="rtl">
          <div>
            <label className={labelClass}>عنوان (تیتر هیرو)</label>
            <input
              className={fieldClass}
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              placeholder="مثلاً گل‌هایی برای ماندن"
            />
          </div>
          <div>
            <label className={labelClass}>زیرعنوان</label>
            <textarea
              className={fieldClass}
              rows={2}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="جمله کوتاه زیر تیتر در هیرو صفحه اصلی"
            />
          </div>
          <div>
            <label className={labelClass}>برچسب بالای تیتر</label>
            <input
              className={fieldClass}
              value={form.badge}
              onChange={(e) => setField('badge', e.target.value)}
              placeholder={
                form.placement === 'homepage_promo'
                  ? 'مثلاً Flower Box (برچسب انگلیسی روی بنر)'
                  : 'مثلاً ایوا'
              }
            />
          </div>
          <ImageUploadField
            label="تصویر بنر"
            value={form.image}
            onChange={(url) => setField('image', url)}
            hint={
              form.placement === 'homepage_promo'
                ? 'پرومو: PNG/WebP با بک‌گراند شفاف (کات‌اوت محصول). تصویر از لبه بنر رنگی بیرون می‌زند.'
                : 'هیرو: تصویر تمام‌عرض (~۱۶:۹ یا عریض‌تر) بهتر است.'
            }
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>متن دکمه</label>
              <input
                className={fieldClass}
                value={form.ctaText}
                onChange={(e) => setField('ctaText', e.target.value)}
                placeholder="مشاهده محصولات"
              />
            </div>
            <div>
              <label className={labelClass}>لینک مقصد</label>
              <input
                className={fieldClass}
                value={form.targetUrl}
                onChange={(e) => setField('targetUrl', e.target.value)}
                dir="ltr"
                placeholder="/products"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>جایگاه</label>
              <select
                className={fieldClass}
                value={form.placement}
                onChange={(e) => setField('placement', e.target.value as BannerPlacement)}
              >
                <option value="homepage_hero">هیرو صفحه اصلی</option>
                <option value="homepage_promo">پرومو صفحه اصلی</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>تم رنگی</label>
              <select
                className={fieldClass}
                value={form.theme}
                onChange={(e) => setField('theme', e.target.value as BannerTheme)}
              >
                <option value="teal">آبی روشن (پرومو کات‌اوت)</option>
                <option value="amber">یاسی (پرومو کات‌اوت)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>ترتیب</label>
              <input
                type="number"
                className={fieldClass}
                value={form.order}
                onChange={(e) => setField('order', num(e.target.value))}
                dir="ltr"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField('isActive', e.target.checked)}
              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            فعال — در صفحه اصلی نمایش داده شود
          </label>
        </div>
      </Modal>

      <Modal
        isOpen={viewOpen}
        onClose={closeView}
        title="جزئیات بنر"
        size="lg"
        footer={
          <button
            type="button"
            onClick={closeView}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            بستن
          </button>
        }
      >
        {viewing ? (
          <div className="space-y-4" dir="rtl">
            {viewing.image ? (
              <img
                src={viewing.image}
                alt={viewing.title}
                className="w-full h-48 object-cover rounded-lg border border-slate-200"
              />
            ) : (
              <div className="h-48 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400">
                بدون تصویر
              </div>
            )}
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-slate-500">عنوان</dt>
                <dd className="font-medium text-slate-900 mt-0.5">{viewing.title || '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">جایگاه</dt>
                <dd className="font-medium text-slate-900 mt-0.5">
                  {placementLabels[viewing.placement]}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-slate-500">زیرعنوان</dt>
                <dd className="font-medium text-slate-900 mt-0.5">{viewing.description || '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">برچسب</dt>
                <dd className="font-medium text-slate-900 mt-0.5">{viewing.badge || '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-500">دکمه</dt>
                <dd className="font-medium text-slate-900 mt-0.5">
                  {viewing.ctaText} → {viewing.targetUrl || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">ترتیب</dt>
                <dd className="font-medium text-slate-900 mt-0.5">{viewing.order}</dd>
              </div>
              <div>
                <dt className="text-slate-500">وضعیت</dt>
                <dd className="font-medium text-slate-900 mt-0.5">
                  {viewing.isActive ? 'فعال' : 'غیرفعال'}
                </dd>
              </div>
            </dl>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
